import { useCallback, useEffect, useState } from "react";
import { push as pushApi } from "../api/client";

/**
 * Web Push registration, and an honest account of why it might not be available.
 *
 * iOS is the reason this is a state machine rather than a button. There, Web Push works *only*
 * from a PWA installed to the Home Screen (16.4+), permission must be requested from a real
 * user gesture, and nothing in the API distinguishes "not installed" from "not supported" — so
 * the app has to work that out itself and say which one it is. A bare Enable button that does
 * nothing on a phone is the single worst outcome here.
 */

export type PushStatus =
  | "checking"          // not worked out yet — never show a step based on this
  | "unsupported"       // no service worker or no PushManager at all
  | "needs-install"     // iOS, in a browser tab: must be added to the Home Screen first
  | "unconfigured"      // the server has no VAPID keys
  | "denied"            // the user said no; only Settings can undo it
  | "prompt"            // ready to ask
  | "subscribed";

export const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac; the touch points give it away.
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (window.navigator as unknown as { standalone?: boolean }).standalone === true;

/**
 * VAPID public keys travel as base64url; `applicationServerKey` wants raw bytes.
 *
 * Returns an ArrayBuffer rather than a Uint8Array: since TypeScript 5.7 typed arrays are
 * generic over their backing buffer, and `Uint8Array<ArrayBufferLike>` no longer satisfies
 * `BufferSource`. Allocating the buffer explicitly sidesteps that without a cast.
 */
function vapidKeyToBytes(base64: string): ArrayBuffer {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(padded);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

export function usePush() {
  const [status, setStatus] = useState<PushStatus>("checking");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>("");

  const refresh = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      // On iOS this is what a normal browser tab looks like — the APIs only exist once the app
      // is installed — so the actionable message is "install it", not "your browser can't".
      setStatus(isIos() && !isStandalone() ? "needs-install" : "unsupported");
      return;
    }
    let config: { enabled: boolean };
    try {
      config = await pushApi.config();
    } catch {
      setStatus("unsupported");
      return;
    }
    if (!config.enabled) {
      setStatus("unconfigured");
      return;
    }
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    setStatus(existing ? "subscribed" : "prompt");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    setBusy(true);
    setMessage("");
    try {
      // Must be inside the user-gesture chain on iOS, so this is called straight from onClick
      // with no awaits before it.
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus(permission === "denied" ? "denied" : "prompt");
        return;
      }
      const config = await pushApi.config();
      if (!config.enabled || !config.vapid_public_key) {
        setStatus("unconfigured");
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          // Required to be true by every browser that implements this; a silent push would be
          // rejected anyway.
          userVisibleOnly: true,
          applicationServerKey: vapidKeyToBytes(config.vapid_public_key),
        }));
      await pushApi.subscribe(subscription.toJSON());
      setStatus("subscribed");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    setMessage("");
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Tell the server first: if unsubscribing locally succeeded and the call failed, the
        // server would keep pushing to an endpoint nothing listens to.
        await pushApi.unsubscribe(subscription.endpoint).catch(() => undefined);
        await subscription.unsubscribe();
      }
      setStatus("prompt");
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setBusy(true);
    setMessage("");
    try {
      const result = await pushApi.test();
      setMessage(
        result.delivered > 0
          ? `Sent to ${result.delivered} device${result.delivered === 1 ? "" : "s"}.`
          : (result.message ?? "No device accepted it."),
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }, []);

  return { status, busy, message, enable, disable, sendTest, refresh };
}
